import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Mail, Plus, Trash2, Upload, Users, ChevronRight, X, 
  Search, Tag, FileSpreadsheet, UserPlus, Edit2, Save
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { AppNavbar } from '../components/AppNavbar';

export const EmailListsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef();
  
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedList, setSelectedList] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAddSubscriberDialog, setShowAddSubscriberDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // New list form
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  
  // New subscriber form
  const [newSubscriber, setNewSubscriber] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    city: '',
    zip_code: '',
    tags: '',
    notes: ''
  });
  
  // Search
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    try {
      const response = await apiClient.get('/email-lists');
      setLists(response.data.lists || []);
    } catch (error) {
      console.error('Failed to fetch lists');
    } finally {
      setLoading(false);
    }
  };

  const fetchListDetails = async (listId) => {
    try {
      const response = await apiClient.get(`/email-lists/${listId}`);
      setSelectedList(response.data);
    } catch (error) {
      toast.error('Failed to load list details');
    }
  };


  const createList = async () => {
    if (!newListName.trim()) {
      toast.error('Please enter a list name');
      return;
    }
    
    try {
      await apiClient.post('/email-lists', {
        name: newListName,
        description: newListDescription
      });
      toast.success('Email list created');
      setNewListName('');
      setNewListDescription('');
      setShowCreateDialog(false);
      fetchLists();
    } catch (error) {
      toast.error('Failed to create list');
    }
  };

  const deleteList = async (listId) => {
    if (!window.confirm('Are you sure you want to delete this list?')) return;
    
    try {
      await apiClient.delete(`/email-lists/${listId}`);
      toast.success('List deleted');
      if (selectedList?.list_id === listId) {
        setSelectedList(null);
      }
      fetchLists();
    } catch (error) {
      toast.error('Failed to delete list');
    }
  };

  const addSubscriber = async () => {
    if (!newSubscriber.email || !newSubscriber.email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    
    try {
      const subscriberData = {
        ...newSubscriber,
        tags: newSubscriber.tags ? newSubscriber.tags.split(',').map(t => t.trim()) : []
      };
      
      await apiClient.post(`/email-lists/${selectedList.list_id}/subscribers`, subscriberData);
      toast.success('Subscriber added');
      setNewSubscriber({
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        address: '',
        city: '',
        zip_code: '',
        tags: '',
        notes: ''
      });
      setShowAddSubscriberDialog(false);
      fetchListDetails(selectedList.list_id);
      fetchLists();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add subscriber');
    }
  };

  const removeSubscriber = async (email) => {
    if (!window.confirm('Remove this subscriber?')) return;
    
    try {
      await apiClient.delete(`/email-lists/${selectedList.list_id}/subscribers/${encodeURIComponent(email)}`);
      toast.success('Subscriber removed');
      fetchListDetails(selectedList.list_id);
      fetchLists();
    } catch (error) {
      toast.error('Failed to remove subscriber');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!selectedList) {
      toast.error('Please select a list first');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await apiClient.post(
        `/email-lists/${selectedList.list_id}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(response.data.message);
      fetchListDetails(selectedList.list_id);
      fetchLists();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const filteredSubscribers = selectedList?.subscribers?.filter(sub => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      sub.email?.toLowerCase().includes(query) ||
      sub.first_name?.toLowerCase().includes(query) ||
      sub.last_name?.toLowerCase().includes(query) ||
      sub.city?.toLowerCase().includes(query) ||
      sub.tags?.some(t => t.toLowerCase().includes(query))
    );
  }) || [];

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-texture" />
      
      <AppNavbar />

      {/* Content */}
      <main className="container mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Lists Sidebar */}
          <div className="w-80 flex-shrink-0">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Email Lists</h2>
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="create-list-btn">
                    <Plus className="h-4 w-4 mr-1" />
                    New List
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Email List</DialogTitle>
                    <DialogDescription>Create a new list to organize your subscribers.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>List Name</Label>
                      <Input
                        value={newListName}
                        onChange={(e) => setNewListName(e.target.value)}
                        placeholder="e.g., Buyers, Sellers, Past Clients"
                        data-testid="new-list-name-input"
                      />
                    </div>
                    <div>
                      <Label>Description (optional)</Label>
                      <Textarea
                        value={newListDescription}
                        onChange={(e) => setNewListDescription(e.target.value)}
                        placeholder="Describe this list..."
                        rows={2}
                      />
                    </div>
                    <Button onClick={createList} className="w-full" data-testid="create-list-submit">
                      Create List
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-2">
              {loading ? (
                <p className="text-muted-foreground text-center py-8">Loading...</p>
              ) : lists.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-border/50 rounded-lg">
                  <Users className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">No email lists yet</p>
                  <p className="text-xs text-muted-foreground">Create your first list to get started</p>
                </div>
              ) : (
                lists.map((list) => (
                  <div
                    key={list.list_id}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedList?.list_id === list.list_id 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border/50 hover:border-primary/50'
                    }`}
                    onClick={() => fetchListDetails(list.list_id)}
                    data-testid={`email-list-${list.list_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{list.name}</p>
                        <p className="text-sm text-muted-foreground">{list.email_count || 0} subscribers</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* List Details */}
          <div className="flex-1">
            {selectedList ? (
              <div className="space-y-6">
                {/* List Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold">{selectedList.name}</h2>
                    {selectedList.description && (
                      <p className="text-muted-foreground">{selectedList.description}</p>
                    )}
                    <p className="text-sm text-muted-foreground mt-1">
                      {selectedList.email_count || selectedList.subscribers?.length || 0} subscribers
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                    <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-csv-btn">
                      <FileSpreadsheet className="h-4 w-4 mr-1" />
                      {uploading ? 'Uploading...' : 'Upload CSV'}
                    </Button>
                    <Dialog open={showAddSubscriberDialog} onOpenChange={setShowAddSubscriberDialog}>
                      <DialogTrigger asChild>
                        <Button size="sm" data-testid="add-subscriber-btn">
                          <UserPlus className="h-4 w-4 mr-1" />
                          Add Subscriber
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader>
                          <DialogTitle>Add Subscriber</DialogTitle>
                          <DialogDescription>Add a new subscriber to {selectedList.name}</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>First Name</Label>
                              <Input
                                value={newSubscriber.first_name}
                                onChange={(e) => setNewSubscriber({...newSubscriber, first_name: e.target.value})}
                                placeholder="John"
                              />
                            </div>
                            <div>
                              <Label>Last Name</Label>
                              <Input
                                value={newSubscriber.last_name}
                                onChange={(e) => setNewSubscriber({...newSubscriber, last_name: e.target.value})}
                                placeholder="Doe"
                              />
                            </div>
                          </div>
                          <div>
                            <Label>Email *</Label>
                            <Input
                              type="email"
                              value={newSubscriber.email}
                              onChange={(e) => setNewSubscriber({...newSubscriber, email: e.target.value})}
                              placeholder="john@example.com"
                              data-testid="subscriber-email-input"
                            />
                          </div>
                          <div>
                            <Label>Phone</Label>
                            <Input
                              value={newSubscriber.phone}
                              onChange={(e) => setNewSubscriber({...newSubscriber, phone: e.target.value})}
                              placeholder="(555) 123-4567"
                            />
                          </div>
                          <div>
                            <Label>Address</Label>
                            <Input
                              value={newSubscriber.address}
                              onChange={(e) => setNewSubscriber({...newSubscriber, address: e.target.value})}
                              placeholder="123 Main St"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>City</Label>
                              <Input
                                value={newSubscriber.city}
                                onChange={(e) => setNewSubscriber({...newSubscriber, city: e.target.value})}
                                placeholder="New York"
                              />
                            </div>
                            <div>
                              <Label>ZIP Code</Label>
                              <Input
                                value={newSubscriber.zip_code}
                                onChange={(e) => setNewSubscriber({...newSubscriber, zip_code: e.target.value})}
                                placeholder="10001"
                              />
                            </div>
                          </div>
                          <div>
                            <Label>Tags (comma separated)</Label>
                            <Input
                              value={newSubscriber.tags}
                              onChange={(e) => setNewSubscriber({...newSubscriber, tags: e.target.value})}
                              placeholder="buyer, luxury, first-time"
                            />
                          </div>
                          <div>
                            <Label>Notes</Label>
                            <Textarea
                              value={newSubscriber.notes}
                              onChange={(e) => setNewSubscriber({...newSubscriber, notes: e.target.value})}
                              placeholder="Any additional notes..."
                              rows={2}
                            />
                          </div>
                          <Button onClick={addSubscriber} className="w-full" data-testid="submit-subscriber-btn">
                            Add Subscriber
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Button variant="destructive" size="sm" onClick={() => deleteList(selectedList.list_id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name, email, city, or tag..."
                    className="pl-10"
                  />
                </div>

                {/* Subscribers Table */}
                <div className="border border-border/50 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-3 text-sm font-medium">Name</th>
                          <th className="text-left p-3 text-sm font-medium">Email</th>
                          <th className="text-left p-3 text-sm font-medium">Phone</th>
                          <th className="text-left p-3 text-sm font-medium">City</th>
                          <th className="text-left p-3 text-sm font-medium">Tags</th>
                          <th className="text-left p-3 text-sm font-medium w-16"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSubscribers.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center py-8 text-muted-foreground">
                              {searchQuery ? 'No subscribers match your search' : 'No subscribers yet'}
                            </td>
                          </tr>
                        ) : (
                          filteredSubscribers.map((sub, idx) => (
                            <tr key={idx} className="border-t border-border/30 hover:bg-muted/20">
                              <td className="p-3 text-sm">
                                {sub.first_name || sub.last_name 
                                  ? `${sub.first_name || ''} ${sub.last_name || ''}`.trim()
                                  : '-'}
                              </td>
                              <td className="p-3 text-sm">{sub.email}</td>
                              <td className="p-3 text-sm">{sub.phone || '-'}</td>
                              <td className="p-3 text-sm">{sub.city || '-'}</td>
                              <td className="p-3 text-sm">
                                <div className="flex flex-wrap gap-1">
                                  {sub.tags?.map((tag, i) => (
                                    <span key={i} className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td className="p-3 text-sm">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => removeSubscriber(sub.email)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* CSV Template Help */}
                <div className="p-4 bg-muted/20 rounded-lg">
                  <h4 className="font-medium mb-2">CSV Upload Format</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Your CSV file should include a header row. Supported columns:
                  </p>
                  <code className="text-xs bg-muted p-2 rounded block">
                    email, first_name, last_name, phone, address, city, zip, tags, notes
                  </code>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-96 border border-dashed border-border/50 rounded-lg">
                <div className="text-center">
                  <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-medium mb-2">Select an Email List</h3>
                  <p className="text-sm text-muted-foreground">Choose a list from the sidebar to view and manage subscribers</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
